import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FilterX } from "lucide-react";
import { FormService, FormField } from "@shared/services/FormServices";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";

interface PhotoMeterFilters {
  month: string;
  status: string;
  sortBy: string;
  startDate: string;
  endDate: string;
}

interface CycleListFiltersProps {
  onSubmit: (filters: Record<string, string>) => void;
}

const CycleListFilters: React.FC<CycleListFiltersProps> = ({ onSubmit }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract current URL filter params
  const monthParam = searchParams.get("month") || "all";
  const statusParam = searchParams.get("status") || "all";
  const sortByParam = searchParams.get("sortBy") || "all";
  const startDateParam = searchParams.get("startDate") || "";
  const endDateParam = searchParams.get("endDate") || "";

  const [filters, setFilters] = useState<PhotoMeterFilters>({
    month: decodeURIComponent(monthParam),
    status: decodeURIComponent(statusParam),
    sortBy: decodeURIComponent(sortByParam),
    startDate: startDateParam,
    endDate: endDateParam,
  });

  // --- Fetch dropdown options ---
  const { data: runStatus } = useChoices({ choice: "run_status" });
  const runStatusList = mapToLabelValue(runStatus);

  const monthOptions = useMemo(
    () => [
      { label: "All Months", value: "all" },
      { label: "January", value: "january" },
      { label: "February", value: "february" },
      { label: "March", value: "march" },
      { label: "April", value: "april" },
      { label: "May", value: "may" },
      { label: "June", value: "june" },
      { label: "July", value: "july" },
      { label: "August", value: "august" },
      { label: "September", value: "september" },
      { label: "October", value: "october" },
      { label: "November", value: "november" },
      { label: "December", value: "december" },
    ],
    []
  );

  const statusOptions = useMemo(() => {
    const base = [{ label: "All Statuses", value: "all" }];
    if (runStatusList?.length)
      base.push(
        ...runStatusList.map((s) => ({
          label: s.label,
          value: String(s.value),
        }))
      );
    return base;
  }, [runStatusList]);

  const sortOptions = useMemo(
    () => [
      { label: "No Sorting", value: "all" },
      { label: "Next Run Date", value: "next_run_date" },
      { label: "Last Run Date", value: "last_run_date" },
    ],
    []
  );

  // --- Create form fields ---
  const createFormFields = (): FormField[] => [
    {
      name: "month",
      type: "select",
      label: "Month",
      options: monthOptions,
      placeholder: "Select month",
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      options: statusOptions,
      placeholder: "Select status",
    },
    {
      name: "sortBy",
      type: "select",
      label: "Sort By",
      options: sortOptions,
      placeholder: "Select sorting",
    },
    {
      name: "startDate",
      type: "datepicker",
      label: "Start Date",
      placeholder: "Select start date",
    },
    {
      name: "endDate",
      type: "datepicker",
      label: "End Date",
      placeholder: "Select end date",
    },
  ];

  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: filters,
  });

  // --- Sync URL params -> form ---
  useEffect(() => {
    const current = {
      month: decodeURIComponent(monthParam),
      status: decodeURIComponent(statusParam),
      sortBy: decodeURIComponent(sortByParam),
      startDate: startDateParam,
      endDate: endDateParam,
    };
    setFilters(current);
    form.reset(current);
  }, [monthParam, statusParam, sortByParam, startDateParam, endDateParam, form]);

  // --- Handlers ---
  const handleSubmit = (data: any) => {
    const params: Record<string, string> = {};
    if (data.month && data.month !== "all") params.month = data.month;
    if (data.status && data.status !== "all") params.status = data.status;
    if (data.sortBy && data.sortBy !== "all") params.sortBy = data.sortBy;
    if (data.startDate) params.startDate = data.startDate;
    if (data.endDate) params.endDate = data.endDate;

    onSubmit(params);
  };

  const handleReset = () => {
    const defaults = {
      month: "all",
      status: "all",
      sortBy: "all",
      startDate: "",
      endDate: "",
    };
    setFilters(defaults);
    form.reset(defaults);
    setSearchParams({});
    onSubmit({});
  };

  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-3 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        <FilterX className="w-4 h-4 mr-1" /> Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      config={{
        showSubmitButton: false,
        showResetButton: false,
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={3}
      responsiveColumns={{ sm: 1, md: 2, lg: 3 }}
      className="space-y-4"
      renderCustomFooter={renderCustomFooter}
    />
  );
};

export default CycleListFilters;
